import { useEffect } from 'react';
import {
  Calendar,
  Phone,
  Mail,
  MessageSquare,
  Users,
  FileText,
  MoreHorizontal,
  AlertTriangle,
} from 'lucide-react';
import { useActivityStore } from '../../store/activityStore';
import type { Activity } from '../../types';

interface UpcomingActivitiesProps {
  personId: string;
  onComplete: (activity: Activity) => void;
  onCancel: (activityId: string) => void;
}

const ACTIVITY_ICONS: Record<string, typeof Phone> = {
  CALL: Phone,
  TEXT: MessageSquare,
  EMAIL: Mail,
  IN_PERSON: Users,
  NOTE: FileText,
  OTHER: MoreHorizontal,
};

const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  CALL: 'Call',
  TEXT: 'Text',
  EMAIL: 'Email',
  IN_PERSON: 'In Person',
  NOTE: 'Note',
  OTHER: 'Other',
};

/**
 * Format a date as "Thu Mar 27 at 2:00 PM"
 */
const formatScheduledDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.getDate();
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${dayOfWeek} ${month} ${day} at ${time}`;
};

/**
 * Check if a date is in the past (overdue)
 */
const isOverdue = (dateStr: string): boolean => {
  return new Date(dateStr) < new Date();
};

/**
 * Displays upcoming (PLANNED) activities for a person.
 * Renders nothing if there are no upcoming activities.
 */
export const UpcomingActivities = ({
  personId,
  onComplete,
  onCancel,
}: UpcomingActivitiesProps) => {
  const { upcomingActivities, isLoadingUpcoming, fetchUpcoming, openActivityDetail } =
    useActivityStore();

  useEffect(() => {
    fetchUpcoming(personId);
  }, [personId, fetchUpcoming]);

  // Don't render anything if no upcoming activities
  if (isLoadingUpcoming || upcomingActivities.length === 0) {
    return null;
  }

  const handleCancel = (activityId: string) => {
    if (window.confirm('Cancel this scheduled activity?')) {
      onCancel(activityId);
    }
  };

  return (
    <div className="card p-6 space-y-3">
      {/* Section header */}
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-gray-400" />
        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Upcoming
        </h3>
      </div>

      {/* Activity cards */}
      {upcomingActivities.map((activity) => {
        const Icon = ACTIVITY_ICONS[activity.type] || MoreHorizontal;
        const typeLabel = ACTIVITY_TYPE_LABELS[activity.type] || activity.type;
        const scheduledDate = activity.scheduledAt;
        const overdue = scheduledDate ? isOverdue(scheduledDate) : false;

        return (
          <div
            key={activity.id}
            onClick={() => openActivityDetail(activity.id)}
            className="border-l-2 border-dashed border-blast-400 pl-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded-md"
          >
            {/* Type + date row */}
            <div className="flex items-center gap-2 flex-wrap">
              <Icon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {typeLabel}
              </span>
              <span className="text-sm text-gray-400 dark:text-gray-500">
                &middot;
              </span>
              <span
                className={`text-sm ${
                  overdue
                    ? 'text-red-600 dark:text-red-400 font-medium'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {scheduledDate ? formatScheduledDate(scheduledDate) : 'No date'}
              </span>
              {overdue && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                  <AlertTriangle className="h-3 w-3" />
                  OVERDUE
                </span>
              )}
            </div>

            {/* Summary */}
            {activity.summary && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {activity.summary}
              </p>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-3 mt-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onComplete(activity);
                }}
                className="text-sm font-medium text-blast-600 hover:text-blast-700 dark:text-blast-400 dark:hover:text-blast-300 transition-colors"
              >
                Complete
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel(activity.id);
                }}
                className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
